import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from '../../components/PageContainer';
import PillowSegmentation from '../../pillow-segmentation-widget';
import { API_URL } from '../../constants';
import { IStore } from '../../store/types';
import { getFreshAccessToken } from '../../api/auth';
import { setAuthenticatedUser } from '../../store/slices/auth';

// This page is meant to stay open for up to ~12h/day. Cognito access tokens
// typically expire after ~1h, so we proactively keep the session alive in the
// background instead of waiting for the backend to reject a stale token.
const BACKGROUND_REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

const PillowSegmentationScreen = () => {
  const dispatch = useDispatch();
  const { authData, themeMode, userInfo } = useSelector<IStore, {
    authData: Record<string, any>;
    themeMode: 'light' | 'dark';
    userInfo: { name: string; role: string; allRoles: string[] };
  }>(s => ({
    authData: (s.auth.data as any) ?? {},
    themeMode: s.theme.mode,
    userInfo: {
      name:     (s.auth.data as any).name     ?? '',
      role:     (s.auth.data as any).role     ?? '',
      allRoles: (s.auth.data as any).allRoles ?? [],
    },
  }));

  // Keep the latest known-good token around for requests, and the full auth
  // payload so a refresh only ever updates the `token` field.
  const tokenRef = useRef<string>(authData.token ?? '');
  const authDataRef = useRef(authData);
  authDataRef.current = authData;

  // Asks Cognito (via Amplify) for the current session. This is the
  // documented way to get a guaranteed non-expired accessToken: Amplify
  // silently calls the refresh-token flow under the hood whenever the
  // cached session is expired, and just returns the cached one otherwise.
  // No sign-out, no redirect, no page reload — the widget below never
  // unmounts and its state (uploaded image, camera, search results, model
  // session, etc.) is untouched.
  const ensureFreshToken = useCallback(async (): Promise<string> => {
    try {
      const freshToken = await getFreshAccessToken();
      if (freshToken && freshToken !== tokenRef.current) {
        tokenRef.current = freshToken;
        dispatch<any>((setAuthenticatedUser as any)({ ...authDataRef.current, token: freshToken }));
      }
      return tokenRef.current;
    } catch (error) {
      // Refresh genuinely failed (e.g. refresh token itself expired/revoked).
      // Fall back to whatever token we currently have; the request will
      // surface the real 401 instead of silently hanging.
      console.error('[PillowSegmentation] Failed to refresh Cognito session', error);
      return tokenRef.current;
    }
  }, [dispatch]);

  useEffect(() => {
    // Refresh once on mount, then keep the session alive on a timer for as
    // long as this page stays open — independent of whether the user is
    // actively triggering searches.
    ensureFreshToken();
    const intervalId = setInterval(ensureFreshToken, BACKGROUND_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [ensureFreshToken]);

  const authHeaders = useCallback(async () => {
    const freshToken = await ensureFreshToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${encodeURIComponent(freshToken)}`,
    };
  }, [ensureFreshToken]);

  // A generic 401-retry wrapper: if the backend still rejects the token as
  // unauthorized (e.g. a refresh race or clock skew), force one more Cognito
  // refresh and retry the request exactly once before giving up. This keeps
  // the page usable for its whole ~12h session without ever bouncing the
  // user to a logout/reload.
  const fetchWithAuthRetry = useCallback(async (url: string, init: RequestInit) => {
    let res = await fetch(url, { ...init, headers: await authHeaders() });
    if (res.status === 401) {
      // Invalidate our cached token so ensureFreshToken() is forced to ask
      // Cognito again instead of trusting the (apparently stale) cached one.
      tokenRef.current = '';
      res = await fetch(url, { ...init, headers: await authHeaders() });
    }
    return res;
  }, [authHeaders]);

  const searchApi = useCallback(async (imageB64: string) => {
    const res = await fetchWithAuthRetry(`https://kbnx1osgjg.execute-api.us-west-1.amazonaws.com/prod/admin-api/aav/image-vector/search?limit=30`, {
      method: 'POST',
      body: JSON.stringify({ imageB64 }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json.results ?? json.data ?? json ?? [];
  }, [fetchWithAuthRetry]);

  const logApi = useCallback(async (payload: {
    imageDataUrl?: string;
    device?: string;
    logData?: object;
  }) => {
    const res = await fetchWithAuthRetry(`${API_URL}/aav/model-front-logs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Log HTTP ${res.status}`);
    return res.json();
  }, [fetchWithAuthRetry]);

  return (
    <PageContainer contentStyle={{ padding: 0, overflow: 'auto' }}>
      <PillowSegmentation searchApi={searchApi} logApi={logApi} themeMode={themeMode} userInfo={userInfo as any} />
    </PageContainer>
  );
};

export default PillowSegmentationScreen;
